import { useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { Clock, Eye } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import {
  OpensBarChart,
  buildChartData,
  buildStatsRange,
} from '@modules/_shared/components/document-stats/OpensBarChart';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { IconBadge } from '@modules/_shared/shared/components/IconBadge';
import { toHumanReadableDate } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';

const CHART_RANGE_DAYS = 30;

const GET_DOCUMENT_RECIPIENT_STATUS = gql(/* GraphQL */ `
  query GetDocumentRecipientStatus(
    $documentId: String!
    $documentType: String
    $recipientUserId: uuid!
  ) {
    document_recipient_status(
      document_id: $documentId
      document_type: $documentType
      recipient_user_id: $recipientUserId
    ) {
      shared
      shared_at
      last_open_at
      total_opens
      unique_opens
      unidentified_opens
      daily {
        date
        total
        unique
      }
    }
  }
`);

export const BuyerLeadBrochureCard: React.FC<{
  lotId: string | null;
  userId: string;
}> = ({ lotId, userId }) => {
  const { t, locale, dateLocale } = useLocale();
  const [currentError, setCurrentError] = useState<ErrorLike | null>(null);

  const documentId = useMemo(
    () => (lotId != null ? toGlobalId('Lot', lotId) : ''),
    [lotId],
  );
  const statsRange = useMemo(() => buildStatsRange(CHART_RANGE_DAYS), []);

  const { data, loading, error } = useQuery(GET_DOCUMENT_RECIPIENT_STATUS, {
    variables: {
      documentId,
      documentType: Doc_Template_Type_Enum_Enum.LotBrochure,
      recipientUserId: userId,
    },
    skip: lotId == null,
    fetchPolicy: 'cache-and-network',
  });

  const status = data?.document_recipient_status;
  const shared = lotId != null && status?.shared === true;
  const totalOpens = status?.total_opens ?? 0;
  const lastOpenAt = status?.last_open_at;

  useEffect(() => {
    if (error != null) {
      setCurrentError(error);
    }
  }, [error]);

  const sharedOn = useMemo(() => {
    if (status?.shared_at == null) {
      return null;
    }
    try {
      return format(parseISO(status.shared_at), 'd MMM yyyy', {
        locale: dateLocale,
      });
    } catch {
      return null;
    }
  }, [status?.shared_at, dateLocale]);

  const chartData = buildChartData(
    status?.daily ?? [],
    CHART_RANGE_DAYS,
    statsRange.to,
  );

  // No brochure tracking link for this buyer — hide the card entirely rather
  // than showing an empty state (also hides while loading to avoid a flash).
  return (
    <>
      {shared && (
        <ListingAccordion
          title={t('Brochure')}
          headerAction={
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 500 }}
            >
              {t('Last {{days}} days', { days: CHART_RANGE_DAYS })}
            </Typography>
          }
          defaultExpanded
          loading={loading && data == null}
        >
          <Stack spacing={2}>
            {sharedOn != null && (
              <Typography variant="caption" color="text.secondary">
                {t('Brochure link created on {{date}}', { date: sharedOn })}
              </Typography>
            )}
            <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
              <IconBadge
                icon={Eye}
                color="primary.main"
                backgroundColor="primary.lighter"
              />
              <Stack
                direction="row"
                spacing={1.25}
                sx={{ alignItems: 'baseline' }}
              >
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {totalOpens}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('Brochure views')}
                </Typography>
              </Stack>
            </Stack>
            <Divider />
            <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
              <IconBadge
                icon={Clock}
                color="primary.main"
                backgroundColor="primary.lighter"
              />
              <Stack
                direction="row"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('Last opened')}
                </Typography>
                <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                  {lastOpenAt == null
                    ? '—'
                    : toHumanReadableDate(locale, lastOpenAt, {
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                </Typography>
              </Stack>
            </Stack>
            {totalOpens > 0 && (
              <Box
                sx={{
                  width: 'calc(100% + 24px)',
                  transform: 'translateX(-24px)',
                }}
              >
                <OpensBarChart
                  data={chartData}
                  totalOpensLabel={t('Total opens')}
                  uniqueOpensLabel={t('Unique opens')}
                  locale={locale}
                  height={140}
                  showUniqueSplit={false}
                />
              </Box>
            )}
          </Stack>
        </ListingAccordion>
      )}
      <MutationErrorSnackbar
        error={currentError}
        onClose={() => setCurrentError(null)}
      />
    </>
  );
};
